import { useState, useEffect } from 'react';
import {
  SEARCH_STEPS,
  STEP_STATUS,
  AUTO_PROGRESS_DELAY,
} from '../constants/searchSteps';

const MAX_STEPS = 5;
const FIRST_STEP = 1;
const INITIAL_SELECTIONS = {
  'treatment_cultures.culture': null,
  'treatment_cultures.cultural_practices': null,
  treatment_type: null,
  application_type: null,
  intervention_period: null,
};

export const useStepManagement = () => {
  const [currentStep, setCurrentStep] = useState(FIRST_STEP);
  // eslint-disable-next-line
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [selections, setSelections] = useState(INITIAL_SELECTIONS);
  const [selectionLabels, setSelectionLabels] = useState({});

  const findStep = (stepKey) => SEARCH_STEPS.find((s) => s.key === stepKey);

  const hasValidSelections = (value) =>
    value && Array.isArray(value) && value.length > 0;

  const isStepRequired = (stepKey) => {
    const step = findStep(stepKey);
    if (!step) return false;

    // Handle dynamic required function
    if (typeof step.required === 'function') {
      const allSelections = {
        ...selections,
        _labels: selectionLabels,
      };
      return step.required(allSelections);
    }

    // Handle static boolean
    return step.required;
  };

  const isStepValid = (stepKey, value = null) => {
    const step = findStep(stepKey);
    if (!step) return false;

    // If step is not required, it's always valid (can be skipped)
    if (!isStepRequired(stepKey)) return true;

    const checkValue = value !== null ? value : selections[step.field];
    return hasValidSelections(checkValue);
  };

  const getMaxAvailableStep = () => {
    for (let i = FIRST_STEP; i <= MAX_STEPS; i++) {
      if (i === FIRST_STEP) continue;
      if (!isStepValid(i - 1)) return i - 1;
    }
    return MAX_STEPS;
  };

  const canAccessStep = (stepKey) => {
    if (stepKey === FIRST_STEP) return true;
    return isStepValid(stepKey - 1);
  };

  const areAllRequiredStepsValid = () => {
    return SEARCH_STEPS.every((step) => {
      if (!isStepRequired(step.key)) return true;
      return isStepValid(step.key);
    });
  };

  const getStepStatus = (stepKey) => {
    if (completedSteps.has(stepKey)) return STEP_STATUS.COMPLETED;
    if (stepKey === currentStep) return STEP_STATUS.ACTIVE;
    if (canAccessStep(stepKey)) return STEP_STATUS.AVAILABLE;
    return STEP_STATUS.DISABLED;
  };

  const updateSelections = (step, values) => {
    setSelections((prev) => ({
      ...prev,
      [step.field]: values,
    }));
  };

  const updateSelectionLabels = (step, selectedItems) => {
    const labels =
      selectedItems && selectedItems.length > 0 ? selectedItems : [];
    setSelectionLabels((prev) => ({
      ...prev,
      [step.field]: labels,
    }));
  };

  const updateCompletedSteps = (stepKey, values) => {
    setCompletedSteps((prev) => {
      // eslint-disable-next-line
      const newCompleted = new Set(prev);
      if (isStepValid(stepKey, values)) {
        newCompleted.add(stepKey);
      } else {
        newCompleted.delete(stepKey);
        for (let i = stepKey + 1; i <= MAX_STEPS; i++) {
          newCompleted.delete(i);
        }
      }
      return newCompleted;
    });
  };

  const handleStepSkip = (stepKey) => {
    const step = findStep(stepKey);
    if (!step || isStepRequired(stepKey)) return;

    // Clear any existing selections for this step
    updateSelections(step, []);
    updateSelectionLabels(step, []);

    // Mark step as completed (skipped)
    setCompletedSteps((prev) => {
      // eslint-disable-next-line
      const newCompleted = new Set(prev);
      newCompleted.add(stepKey);
      return newCompleted;
    });

    // Move to next step if not the last one
    if (stepKey < MAX_STEPS) {
      setCurrentStep(stepKey + 1);
    }
  };

  const handleStepChange = (stepKey, values, selectedItems) => {
    const step = findStep(stepKey);
    
    updateSelections(step, values);
    updateSelectionLabels(step, selectedItems);
    updateCompletedSteps(stepKey, values);
  };

  const handleStepClick = (stepKey) => {
    const maxAvailable = getMaxAvailableStep();
    if (stepKey <= maxAvailable) {
      setCurrentStep(stepKey);
    }
  };

  const getCurrentStep = () => findStep(currentStep);

  const shouldAutoProgress = () => {
    const step = getCurrentStep();
    return (
      currentStep < MAX_STEPS &&
      isStepValid(currentStep) &&
      currentStep === getMaxAvailableStep() &&
      step?.autoProgress === true
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldAutoProgress()) {
        setCurrentStep(currentStep + 1);
      }
    }, AUTO_PROGRESS_DELAY);

    return () => clearTimeout(timer);
  }, [currentStep, selections, completedSteps]);

  const forceStepRevalidation = () => {
    // eslint-disable-next-line
    setCompletedSteps((prev) => {
      // eslint-disable-next-line
      const newCompleted = new Set();
      for (let i = FIRST_STEP; i <= MAX_STEPS; i++) {
        if (isStepValid(i)) {
          newCompleted.add(i);
        }
      }
      return newCompleted;
    });
  };

  const handleReset = () => {
    setCurrentStep(FIRST_STEP);
    //eslint-disable-next-line
    setCompletedSteps(new Set());
    setSelections(INITIAL_SELECTIONS);
    setSelectionLabels({});
  };

  return {
    currentStep,
    completedSteps,
    selections,
    selectionLabels,
    setCurrentStep,
    isStepValid,
    isStepRequired,
    areAllRequiredStepsValid,
    canAccessStep,
    getStepStatus,
    handleStepChange,
    handleStepSkip,
    handleStepClick,
    handleReset,
    forceStepRevalidation,
  };
};
